// ui/composables/useBillingWorklist.js
import { ref } from 'vue';

import { loadBillingItems } from '../../infrastructure/api/billingItemApi';

/**
 * A clinic's billing items for a period, as the worklist works with them.
 *
 * THE CONTEXT'S COMPOSITION ROOT for billing items — the one place that binds the Firestore adapter
 * to the repository port. Everything above depends on the port; nothing above imports
 * `infrastructure/`. The same role `useChargeMaster` plays for sheets.
 *
 * THE WINDOW IS THE ONLY THING THE QUERY KNOWS. Payer, status and export state narrow the rows in
 * memory here rather than in Firestore, because Firestore allows one range field per query and the
 * range is the service date — so every filter pushed down would be another composite index for a
 * clinic's month, which is a few hundred rows the browser filters for free. Move one down only when
 * a real clinic's period stops fitting in memory, and ship its index first.
 *
 * IT LOADS AND NOTHING ELSE. Narrowing — by period, by payer, by status, by export state — belongs
 * to the table, in memory, so changing any of it never re-reads Firestore. This exposes no
 * filtering of its own on purpose: two places narrowing the same list is how a screen comes to
 * disagree with its own export about what a biller is looking at.
 */

// eslint-disable-next-line import/prefer-default-export
export function useBillingWorklist() {
  /** @type {import('vue').Ref<import('../../domain/model/BillingItem.types').BillingItem[]>} */
  const items = ref([]);
  const loading = ref(false);

  /**
   * Load one clinic's items.
   *
   * IT REPLACES RATHER THAN APPENDS, and clears before the read rather than after: a failed read
   * must not leave the last clinic's rows on display.
   *
   * A FAILURE IS THROWN, NOT STORED. The caller has to say something about it — an empty table and
   * a failed read look identical — and holding an error here as well would be two ways to learn the
   * same thing, free to disagree the moment somebody forgets to await.
   */
  async function load({ clinicId }) {
    loading.value = true;
    items.value = [];

    try {
      items.value = await loadBillingItems({ clinicId });
    } finally {
      loading.value = false;
    }
  } // load()

  return {
    items,
    loading,
    load,
  };
} // useBillingWorklist()
